import { api } from '../api'
import type {
  DeleteFolderResult,
  GetConversationResult,
  InboxFolderListings,
  ListConversationsResult,
  MoveConversationResult,
  SaveFolderResult,
  SendReplyBody,
  SendReplyResult,
} from '../types'

/** `folderId` absent or null is the inbox itself — what is filed in no folder. */
export interface ListConversationsArgs {
  limit?: number
  folderId?: string | null
  /** The switcher's client (BIBLE §19); absent or null is every client's. */
  clientId?: string | null
}

/**
 * What a folder is saved as. A category folder carries the description Jev files by;
 * `is_positive` makes a reply filed there count as a positive one (BIBLE §5, §19.5).
 */
export interface FolderFields {
  name: string
  description: string | null
  is_positive: boolean
}

/**
 * A reply with files is multipart — RTK Query passes `FormData` through untouched and the
 * browser writes the boundary. One without is URL-encoded: the route's `Form` fields read
 * either, and a reply with nothing to upload has no reason to be multipart.
 */
export function sendReplyRequest({ conversation_id, body, attachments = [] }: SendReplyBody) {
  if (attachments.length === 0) {
    return {
      url: '/inbox/reply',
      method: 'POST' as const,
      body: new URLSearchParams({ conversation_id, body }),
    }
  }
  const form = new FormData()
  form.append('conversation_id', conversation_id)
  form.append('body', body)
  for (const file of attachments) form.append('attachments', file)
  return { url: '/inbox/reply', method: 'POST' as const, body: form }
}

export const inboxApi = api.injectEndpoints({
  endpoints: (build) => ({
    listConversations: build.query<ListConversationsResult, ListConversationsArgs | void>({
      query: (args) => ({
        url: '/inbox',
        params: {
          limit: args?.limit ?? 50,
          ...(args?.folderId ? { folder_id: args.folderId } : {}),
          ...(args?.clientId ? { client_id: args.clientId } : {}),
        },
      }),
      providesTags: [{ type: 'Conversation', id: 'LIST' }],
    }),

    listFolders: build.query<InboxFolderListings, void>({
      query: () => '/inbox/folders',
      providesTags: [{ type: 'InboxFolder', id: 'LIST' }],
    }),

    // Every field travels on every save: PUT replaces the folder, so a missing description
    // or flag would clear it. `description` null means a folder filed into by hand.
    createFolder: build.mutation<SaveFolderResult, FolderFields>({
      query: ({ name, description, is_positive }) => ({
        url: '/inbox/folders',
        method: 'POST',
        body: { name, description, is_positive },
      }),
      invalidatesTags: [{ type: 'InboxFolder', id: 'LIST' }],
    }),

    updateFolder: build.mutation<SaveFolderResult, FolderFields & { folderId: string }>({
      query: ({ folderId, name, description, is_positive }) => ({
        url: `/inbox/folders/${folderId}`,
        method: 'PUT',
        body: { name, description, is_positive },
      }),
      invalidatesTags: [{ type: 'InboxFolder', id: 'LIST' }],
    }),

    // What was filed in the folder goes back to the inbox, so both lists change.
    deleteFolder: build.mutation<DeleteFolderResult, string>({
      query: (folderId) => ({ url: `/inbox/folders/${folderId}`, method: 'DELETE' }),
      invalidatesTags: [
        { type: 'InboxFolder', id: 'LIST' },
        { type: 'Conversation', id: 'LIST' },
        'Thread',
      ],
    }),

    moveConversation: build.mutation<
      MoveConversationResult,
      { conversationId: string; folderId: string | null }
    >({
      query: ({ conversationId, folderId }) => ({
        url: `/inbox/${conversationId}/folder`,
        method: 'PUT',
        body: { folder_id: folderId },
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: 'Thread', id: conversationId },
        { type: 'Conversation', id: 'LIST' },
        { type: 'InboxFolder', id: 'LIST' },
      ],
    }),

    getConversation: build.query<GetConversationResult, string>({
      query: (id) => `/inbox/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Thread', id }],
    }),

    sendReply: build.mutation<SendReplyResult, SendReplyBody>({
      query: sendReplyRequest,
      // The message appears the instant you hit send. It is going to a provider over the
      // network and answering somebody who just wrote to you should not feel like waiting
      // — and if the send fails, `undo` puts the thread back exactly as it was.
      async onQueryStarted(
        { conversation_id, body, attachments = [] },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          inboxApi.util.updateQueryData('getConversation', conversation_id, (draft) => {
            draft.messages.push({
              id: `pending-${Date.now()}`,
              conversation_id,
              direction: 'outbound',
              sent_by: 'manual',
              body,
              provider_message_id: '',
              scheduled_send_id: null,
              attachments: attachments.map((file) => ({
                file_name: file.name,
                content_type: file.type,
                size_bytes: file.size,
              })),
              provider_timestamp: new Date().toISOString(),
              created_at: new Date().toISOString(),
            })
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_result, _error, { conversation_id }) => [
        { type: 'Thread', id: conversation_id },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListConversationsQuery,
  useGetConversationQuery,
  useSendReplyMutation,
  useListFoldersQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useMoveConversationMutation,
} = inboxApi
